import React, { useState, useEffect } from "react";
import PageShell from "../../components/layout/PageShell";
import GlassCard from "../../components/ui/GlassCard";
import Modal from "../../components/ui/Modal";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import Button from "../../components/ui/Button";
import Badge from "../../components/ui/Badge";
import { Input, Select, Textarea } from "../../components/ui/FormFields";
import { QuestionTypeMaster } from "../../types";
import { getQuestionTypes, createQuestionType, updateQuestionType, deleteQuestionType } from "../../services/questionTypeService";
import toast from "react-hot-toast";

const QuestionTypeManagementPage: React.FC = () => {
  const [types, setTypes] = useState<QuestionTypeMaster[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingType, setEditingType] = useState<QuestionTypeMaster | null>(null);
  const [deletingType, setDeletingType] = useState<QuestionTypeMaster | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isCodeEdited, setIsCodeEdited] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    name: "",
    code: ""
  });
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    answerFormat: "single_select",
    uiType: "radio",
    evaluationType: "auto",
    scoringMethod: "no_right_wrong",
    usesScaleMaster: false,
    usesTraitMapping: false,
    allowsReverseScoring: false,
    requiresOptions: false,
    requiresCorrectAnswer: false,
    negativeMarking: false,
    requiresMultipleCorrectAnswers: false,
    partialScoringAllowed: false,
    requiresLeftRightItems: false,
    requiresCorrectMapping: false,
    requiresItemList: false,
    requiresCorrectOrder: false,
    requiresCategories: false,
    requiresItems: false,
    requiresCorrectGrouping: false,
    description: "",
    isActive: true
  });

  useEffect(() => {
    loadTypes();
  }, []);

  const loadTypes = async () => {
    setLoading(true);
    setError(null);
    try {
      const fetchedTypes = await getQuestionTypes(false);
      setTypes(fetchedTypes);
      if (fetchedTypes.length === 0) {
        toast.success("No question types found. Create your first one!");
      }
    } catch (error) {
      console.error("Error loading types:", error);
      setError("Failed to load question types. Please check your Firebase configuration.");
      toast.error("Failed to load question types");
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {
      name: "",
      code: ""
    };
    let isValid = true;

    if (!formData.name.trim()) {
      errors.name = "Type name is required";
      isValid = false;
    }

    if (!formData.code.trim()) {
      errors.code = "Code is required";
      isValid = false;
    } else {
      const formattedCode = formData.code.toLowerCase().replace(/\s/g, '_');
      if (!/^[a-z_]+$/.test(formattedCode)) {
        errors.code = "Code can only contain lowercase letters and underscores";
        isValid = false;
      }
    }

    setValidationErrors(errors);
    return isValid;
  };

  const handleSubmit = async () => {
    setShowValidationErrors(true);
    
    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    const formattedCode = formData.code.toLowerCase().replace(/\s/g, '_');
    if (!/^[a-z_]+$/.test(formattedCode)) {
      toast.error("Code can only contain lowercase letters and underscores");
      return;
    }

    try {
      const submitData = {
        name: formData.name,
        code: formattedCode,
        answerFormat: formData.answerFormat,
        uiType: formData.uiType,
        evaluationType: formData.evaluationType,
        scoringMethod: formData.scoringMethod,
        usesScaleMaster: formData.usesScaleMaster,
        usesTraitMapping: formData.usesTraitMapping,
        allowsReverseScoring: formData.allowsReverseScoring,
        requiresOptions: formData.requiresOptions,
        requiresCorrectAnswer: formData.requiresCorrectAnswer,
        negativeMarking: formData.negativeMarking,
        requiresMultipleCorrectAnswers: formData.requiresMultipleCorrectAnswers,
        partialScoringAllowed: formData.partialScoringAllowed,
        requiresLeftRightItems: formData.requiresLeftRightItems,
        requiresCorrectMapping: formData.requiresCorrectMapping,
        requiresItemList: formData.requiresItemList,
        requiresCorrectOrder: formData.requiresCorrectOrder,
        requiresCategories: formData.requiresCategories,
        requiresItems: formData.requiresItems,
        requiresCorrectGrouping: formData.requiresCorrectGrouping,
        description: formData.description,
        isActive: formData.isActive
      };

      if (editingType) {
        await updateQuestionType(editingType.id, submitData);
        toast.success("Question type updated successfully");
      } else {
        await createQuestionType(submitData);
        toast.success("Question type created successfully");
      }
      await loadTypes();
      handleCloseModal();
    } catch (error: any) {
      console.error("Error saving type:", error);
      if (error.message?.includes("already exists")) {
        toast.error("A question type with this code already exists");
      } else {
        toast.error("Failed to save question type");
      }
    }
  };

  const handleDelete = async () => {
    if (!deletingType) return;
    try {
      await deleteQuestionType(deletingType.id);
      toast.success("Question type deleted successfully");
      await loadTypes();
      setDeletingType(null);
    } catch (error) {
      console.error("Error deleting type:", error);
      toast.error("Failed to delete question type");
    }
  };

  const handleEdit = (type: QuestionTypeMaster) => {
    setEditingType(type);
    setFormData({
      name: type.name,
      code: type.code,
      answerFormat: type.answerFormat || "single_select",
      uiType: type.uiType || "radio",
      evaluationType: type.evaluationType || "auto",
      scoringMethod: type.scoringMethod || "no_right_wrong",
      usesScaleMaster: type.usesScaleMaster ?? false,
      usesTraitMapping: type.usesTraitMapping ?? false,
      allowsReverseScoring: type.allowsReverseScoring ?? false,
      requiresOptions: type.requiresOptions ?? false,
      requiresCorrectAnswer: type.requiresCorrectAnswer ?? false,
      negativeMarking: type.negativeMarking ?? false,
      requiresMultipleCorrectAnswers: type.requiresMultipleCorrectAnswers ?? false,
      partialScoringAllowed: type.partialScoringAllowed ?? false,
      requiresLeftRightItems: type.requiresLeftRightItems ?? false,
      requiresCorrectMapping: type.requiresCorrectMapping ?? false,
      requiresItemList: type.requiresItemList ?? false,
      requiresCorrectOrder: type.requiresCorrectOrder ?? false,
      requiresCategories: type.requiresCategories ?? false,
      requiresItems: type.requiresItems ?? false,
      requiresCorrectGrouping: type.requiresCorrectGrouping ?? false,
      description: type.description || "",
      isActive: type.isActive
    });
    setShowValidationErrors(false);
    setValidationErrors({ name: "", code: "" });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingType(null);
    setIsCodeEdited(false);
    setShowValidationErrors(false);
    setValidationErrors({ name: "", code: "" });
    setFormData({
      name: "",
      code: "",
      answerFormat: "single_select",
      uiType: "radio",
      evaluationType: "auto",
      scoringMethod: "no_right_wrong",
      usesScaleMaster: false,
      usesTraitMapping: false,
      allowsReverseScoring: false,
      requiresOptions: false,
      requiresCorrectAnswer: false,
      negativeMarking: false,
      requiresMultipleCorrectAnswers: false,
      partialScoringAllowed: false,
      requiresLeftRightItems: false,
      requiresCorrectMapping: false,
      requiresItemList: false,
      requiresCorrectOrder: false,
      requiresCategories: false,
      requiresItems: false,
      requiresCorrectGrouping: false,
      description: "",
      isActive: true
    });
  };

  const updateDynamicFields = (name: string) => {
    const nameLower = name.toLowerCase();
    if (nameLower.includes("psychometric") || nameLower.includes("scale")) {
      setFormData(prev => ({
        ...prev,
        answerFormat: "single_select",
        uiType: "likert_scale",
        evaluationType: "auto",
        scoringMethod: "trait_based",
        usesScaleMaster: true,
        usesTraitMapping: true,
        allowsReverseScoring: true,
        requiresOptions: false,
        requiresCorrectAnswer: false,
        negativeMarking: false,
        requiresMultipleCorrectAnswers: false,
        partialScoringAllowed: false,
        requiresLeftRightItems: false,
        requiresCorrectMapping: false,
        requiresItemList: false,
        requiresCorrectOrder: false,
        requiresCategories: false,
        requiresItems: false,
        requiresCorrectGrouping: false
      }));
    }
    else if (nameLower.includes("mcq") && nameLower.includes("single")) {
      setFormData(prev => ({
        ...prev,
        answerFormat: "single_select",
        uiType: "radio",
        evaluationType: "auto",
        scoringMethod: "fixed_marks",
        usesScaleMaster: false,
        usesTraitMapping: false,
        allowsReverseScoring: false,
        requiresOptions: true,
        requiresCorrectAnswer: true,
        negativeMarking: false,
        requiresMultipleCorrectAnswers: false,
        partialScoringAllowed: false,
        requiresLeftRightItems: false,
        requiresCorrectMapping: false,
        requiresItemList: false,
        requiresCorrectOrder: false,
        requiresCategories: false,
        requiresItems: false,
        requiresCorrectGrouping: false
      }));
    }
    else if (nameLower.includes("mcq") && nameLower.includes("multiple")) {
      setFormData(prev => ({
        ...prev,
        answerFormat: "multi_select",
        uiType: "checkbox",
        evaluationType: "auto",
        scoringMethod: "partial_full_marks",
        usesScaleMaster: false,
        usesTraitMapping: false,
        allowsReverseScoring: false,
        requiresOptions: true,
        requiresCorrectAnswer: false,
        negativeMarking: false,
        requiresMultipleCorrectAnswers: true,
        partialScoringAllowed: true,
        requiresLeftRightItems: false,
        requiresCorrectMapping: false,
        requiresItemList: false,
        requiresCorrectOrder: false,
        requiresCategories: false,
        requiresItems: false,
        requiresCorrectGrouping: false
      }));
    }
    else if (nameLower.includes("drag") && nameLower.includes("match")) {
      setFormData(prev => ({
        ...prev,
        answerFormat: "mapping",
        uiType: "drag_match",
        evaluationType: "auto",
        scoringMethod: "partial_full_marks",
        usesScaleMaster: false,
        usesTraitMapping: false,
        allowsReverseScoring: false,
        requiresOptions: false,
        requiresCorrectAnswer: false,
        negativeMarking: false,
        requiresMultipleCorrectAnswers: false,
        partialScoringAllowed: true,
        requiresLeftRightItems: true,
        requiresCorrectMapping: true,
        requiresItemList: false,
        requiresCorrectOrder: false,
        requiresCategories: false,
        requiresItems: false,
        requiresCorrectGrouping: false
      }));
    }
    else if (nameLower.includes("drag") && nameLower.includes("sequence")) {
      setFormData(prev => ({
        ...prev,
        answerFormat: "ordering",
        uiType: "drag_reorder",
        evaluationType: "auto",
        scoringMethod: "partial_full_marks",
        usesScaleMaster: false,
        usesTraitMapping: false,
        allowsReverseScoring: false,
        requiresOptions: false,
        requiresCorrectAnswer: false,
        negativeMarking: false,
        requiresMultipleCorrectAnswers: false,
        partialScoringAllowed: true,
        requiresLeftRightItems: false,
        requiresCorrectMapping: false,
        requiresItemList: true,
        requiresCorrectOrder: true,
        requiresCategories: false,
        requiresItems: false,
        requiresCorrectGrouping: false
      }));
    }
    else if (nameLower.includes("drag") && nameLower.includes("categorization")) {
      setFormData(prev => ({
        ...prev,
        answerFormat: "grouping",
        uiType: "drag_categorize",
        evaluationType: "auto",
        scoringMethod: "partial_full_marks",
        usesScaleMaster: false,
        usesTraitMapping: false,
        allowsReverseScoring: false,
        requiresOptions: false,
        requiresCorrectAnswer: false,
        negativeMarking: false,
        requiresMultipleCorrectAnswers: false,
        partialScoringAllowed: true,
        requiresLeftRightItems: false,
        requiresCorrectMapping: false,
        requiresItemList: false,
        requiresCorrectOrder: false,
        requiresCategories: true,
        requiresItems: true,
        requiresCorrectGrouping: true
      }));
    }
  };

  const getAnswerFormatBadge = (format: string) => {
    const formats: Record<string, { label: string; color: string }> = {
      single_select: { label: "Single Select", color: "blue" },
      multi_select: { label: "Multi Select", color: "green" },
      mapping: { label: "Mapping", color: "purple" },
      ordering: { label: "Ordering", color: "orange" },
      grouping: { label: "Grouping", color: "red" },
      text: { label: "Text", color: "purple" },
      numeric: { label: "Numeric", color: "orange" }
    };
    return formats[format] || { label: format, color: "gray" };
  };

  const getUITypeBadge = (uiType: string) => {
    const types: Record<string, { label: string; color: string }> = {
      radio: { label: "Radio Button", color: "blue" },
      checkbox: { label: "Checkbox", color: "green" },
      likert_scale: { label: "Likert Scale", color: "indigo" },
      drag_match: { label: "Drag & Drop Match", color: "purple" },
      drag_reorder: { label: "Drag & Drop Reorder", color: "orange" },
      drag_categorize: { label: "Drag & Drop Categorize", color: "red" },
      scale_radio: { label: "Scale / Radio Button", color: "blue" },
      dropdown: { label: "Dropdown", color: "purple" },
      textarea: { label: "Text Area", color: "orange" },
      number: { label: "Number Input", color: "red" }
    };
    return types[uiType] || { label: uiType, color: "gray" };
  };

  const getScoringMethodBadge = (method: string) => {
    const methods: Record<string, { label: string; color: string }> = {
      no_right_wrong: { label: "No Right/Wrong", color: "gray" },
      trait_based: { label: "Trait-Based Scoring", color: "indigo" },
      fixed_marks: { label: "Fixed Marks", color: "green" },
      partial_full_marks: { label: "Partial / Full Marks", color: "orange" }
    };
    return methods[method] || { label: method, color: "gray" };
  };

  const renderAdditionalSettings = () => {
    const settings = [];

    if (formData.usesScaleMaster) {
      settings.push({ key: "usesScaleMaster", label: "Uses Scale Master", value: formData.usesScaleMaster });
    }
    if (formData.usesTraitMapping) {
      settings.push({ key: "usesTraitMapping", label: "Uses Trait Mapping", value: formData.usesTraitMapping });
    }
    if (formData.allowsReverseScoring) {
      settings.push({ key: "allowsReverseScoring", label: "Reverse Scoring Allowed", value: formData.allowsReverseScoring });
    }
    if (formData.requiresOptions) {
      settings.push({ key: "requiresOptions", label: "Requires Options", value: formData.requiresOptions });
    }
    if (formData.requiresCorrectAnswer) {
      settings.push({ key: "requiresCorrectAnswer", label: "Requires Correct Answer (Facit)", value: formData.requiresCorrectAnswer });
    }
    if (formData.negativeMarking) {
      settings.push({ key: "negativeMarking", label: "Negative Marking", value: formData.negativeMarking });
    }
    if (formData.requiresMultipleCorrectAnswers) {
      settings.push({ key: "requiresMultipleCorrectAnswers", label: "Requires Multiple Correct Answers", value: formData.requiresMultipleCorrectAnswers });
    }
    if (formData.partialScoringAllowed) {
      settings.push({ key: "partialScoringAllowed", label: "Partial Scoring Allowed", value: formData.partialScoringAllowed });
    }
    if (formData.requiresLeftRightItems) {
      settings.push({ key: "requiresLeftRightItems", label: "Requires Left & Right Items", value: formData.requiresLeftRightItems });
    }
    if (formData.requiresCorrectMapping) {
      settings.push({ key: "requiresCorrectMapping", label: "Requires Correct Mapping (Facit)", value: formData.requiresCorrectMapping });
    }
    if (formData.requiresItemList) {
      settings.push({ key: "requiresItemList", label: "Requires Item List", value: formData.requiresItemList });
    }
    if (formData.requiresCorrectOrder) {
      settings.push({ key: "requiresCorrectOrder", label: "Requires Correct Order (Facit)", value: formData.requiresCorrectOrder });
    }
    if (formData.requiresCategories) {
      settings.push({ key: "requiresCategories", label: "Requires Categories", value: formData.requiresCategories });
    }
    if (formData.requiresItems) {
      settings.push({ key: "requiresItems", label: "Requires Items", value: formData.requiresItems });
    }
    if (formData.requiresCorrectGrouping) {
      settings.push({ key: "requiresCorrectGrouping", label: "Requires Correct Grouping (Facit)", value: formData.requiresCorrectGrouping });
    }

    if (settings.length === 0) {
      return <p className="text-sm text-gray-500 italic">No additional settings configured for this question type.</p>;
    }

    return (
      <div className="space-y-3">
        {settings.map(setting => (
          <label key={setting.key} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={setting.value}
              onChange={(e) => setFormData(prev => ({ ...prev, [setting.key]: e.target.checked }))}
              className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
            />
            <span className="text-sm text-gray-700">
              {setting.label} → <span className="font-semibold text-green-600">✅ Yes</span>
            </span>
          </label>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <PageShell title="Question Type Management" subtitle="Manage question types for your assessment system">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell title="Question Type Management" subtitle="Manage question types for your assessment system">
        <GlassCard className="p-8 text-center">
          <div className="text-red-600 mb-4 font-medium">{error}</div>
          <Button onClick={loadTypes}>Retry</Button>
        </GlassCard>
      </PageShell>
    );
  }

  return (
    <PageShell
      title="Question Type Management"
      subtitle="Manage question types for your assessment system"
      action={{ label: "New Question Type", onClick: () => setShowModal(true) }}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {types.map((type) => {
          const answerFormatBadge = getAnswerFormatBadge(type.answerFormat);
          const uiTypeBadge = getUITypeBadge(type.uiType);
          const scoringMethodBadge = getScoringMethodBadge(type.scoringMethod);

          return (
            <GlassCard key={type.id} className="p-5 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-800 text-lg">{type.name}</h3>
                  <p className="text-xs text-gray-500 font-mono mt-1">{type.code}</p>
                </div>
                <Badge text={type.isActive ? "Active" : "Inactive"} variant={type.isActive ? "success" : "secondary"} />
              </div>

              <div className="space-y-2 mb-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge text={answerFormatBadge.label} variant={answerFormatBadge.color as any} size="sm" />
                  <Badge text={uiTypeBadge.label} variant={uiTypeBadge.color as any} size="sm" />
                  <Badge text={scoringMethodBadge.label} variant={scoringMethodBadge.color as any} size="sm" />
                </div>

                {type.description && (
                  <p className="text-xs text-gray-500 mt-2 line-clamp-2">{type.description}</p>
                )}

                <div className="flex items-center gap-3 text-xs text-gray-400 mt-2 flex-wrap">
                  {type.usesScaleMaster && <span>✓ Scale Master</span>}
                  {type.usesTraitMapping && <span>✓ Trait Mapping</span>}
                  {type.allowsReverseScoring && <span>✓ Reverse Scoring</span>}
                  {type.requiresOptions && <span>✓ Options</span>}
                  {type.requiresCorrectAnswer && <span>✓ Correct Answer</span>}
                  {type.requiresMultipleCorrectAnswers && <span>✓ Multi Correct</span>}
                  {type.partialScoringAllowed && <span>✓ Partial Scoring</span>}
                </div>
              </div>

              <div className="flex gap-2 mt-4 pt-3 border-t border-gray-100">
                <Button size="sm" variant="secondary" onClick={() => handleEdit(type)}>
                  Edit
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setDeletingType(type)} className="text-red-600">
                  Delete
                </Button>
              </div>
            </GlassCard>
          );
        })}

        {types.length === 0 && !loading && (
          <div className="col-span-full text-center py-12 text-gray-500">
            No question types found. Click "New Question Type" to create one.
          </div>
        )}
      </div>

      <Modal isOpen={showModal} onClose={handleCloseModal} title={editingType ? "Edit Question Type" : "Create Question Type"} size="xl">
        <div className="space-y-6">
          <div className="space-y-4">
            <h4 className="font-semibold text-gray-700 border-b pb-2">Basic Information</h4>
            <Input
              label="Type Name"
              value={formData.name}
              onChange={(v) => {
                const generatedCode = v
                  .toLowerCase()
                  .trim()
                  .replace(/[^a-z0-9\s]/g, "")
                  .replace(/\s+/g, "_");

                updateDynamicFields(v);

                setFormData({
                  ...formData,
                  name: v,
                  code: isCodeEdited ? formData.code : generatedCode
                });
                
                if (showValidationErrors) {
                  validateForm();
                }
              }}
              placeholder="e.g., MCQ – Single Correct"
              required
              error={showValidationErrors ? validationErrors.name : ""}
            />
            <Input
              label="Code"
              value={formData.code}
              onChange={(v) => {
                setIsCodeEdited(true);
                const formattedCode = v.toLowerCase().replace(/\s/g, "_");
                setFormData({
                  ...formData,
                  code: formattedCode
                });
                
                if (showValidationErrors) {
                  validateForm();
                }
              }}
              placeholder="mcq_single_correct"
              helperText="Use lowercase letters and underscores only"
              required
              error={showValidationErrors ? validationErrors.code : ""}
            />
          </div>

          <div className="space-y-4">
            <h4 className="font-semibold text-gray-700 border-b pb-2">Answer Format</h4>
            <Select
              label="Answer Format *"
              value={formData.answerFormat}
              onChange={(v) => setFormData({ ...formData, answerFormat: v })}
              options={[
                { value: "single_select", label: "Single Select (Scale)" },
                { value: "multi_select", label: "Multi Select" },
                { value: "mapping", label: "Mapping" },
                { value: "ordering", label: "Ordering" },
                { value: "grouping", label: "Grouping" },
                { value: "text", label: "Text" },
                { value: "numeric", label: "Numeric" }
              ]}
            />
          </div>

          <div className="space-y-4">
            <h4 className="font-semibold text-gray-700 border-b pb-2">UI Type</h4>
            <Select
              label="UI Type *"
              value={formData.uiType}
              onChange={(v) => setFormData({ ...formData, uiType: v })}
              options={[
                { value: "radio", label: "Radio Button" },
                { value: "checkbox", label: "Checkbox" },
                { value: "likert_scale", label: "Scale / Likert Style" },
                { value: "drag_match", label: "Drag & Drop Matching UI" },
                { value: "drag_reorder", label: "Drag to reorder" },
                { value: "drag_categorize", label: "Drag into categories" }
              ]}
            />
          </div>

          <div className="space-y-4">
            <h4 className="font-semibold text-gray-700 border-b pb-2">Evaluation & Scoring</h4>
            <Select
              label="Evaluation Type *"
              value={formData.evaluationType}
              onChange={(v) => setFormData({ ...formData, evaluationType: v })}
              options={[
                { value: "auto", label: "Auto" },
                { value: "manual", label: "Manual" }
              ]}
            />
            <Select
              label="Scoring Method *"
              value={formData.scoringMethod}
              onChange={(v) => setFormData({ ...formData, scoringMethod: v })}
              options={[
                { value: "no_right_wrong", label: "No Right/Wrong" },
                { value: "trait_based", label: "Trait-Based Scoring" },
                { value: "fixed_marks", label: "Fixed Marks" },
                { value: "partial_full_marks", label: "Partial / Full Marks" }
              ]}
            />
          </div>

          <div className="space-y-4">
            <h4 className="font-semibold text-gray-700 border-b pb-2">Additional Settings</h4>
            {renderAdditionalSettings()}
          </div>

          <div className="space-y-4">
            <h4 className="font-semibold text-gray-700 border-b pb-2">Description</h4>
            <Textarea
              label="Description"
              value={formData.description}
              onChange={(v) => setFormData({ ...formData, description: v })}
              placeholder="Describe the purpose and usage of this question type"
              rows={3}
            />
          </div>

          <div className="space-y-4">
            <h4 className="font-semibold text-gray-700 border-b pb-2">Status</h4>
            <Select
              label="Status"
              value={formData.isActive ? "active" : "inactive"}
              onChange={(v) => setFormData({ ...formData, isActive: v === "active" })}
              options={[
                { value: "active", label: "Active" },
                { value: "inactive", label: "Inactive" }
              ]}
            />
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button onClick={handleSubmit}>
              {editingType ? "Update" : "Create"}
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!deletingType}
        onClose={() => setDeletingType(null)}
        onConfirm={handleDelete}
        title="Delete Question Type"
        message={`Are you sure you want to delete "${deletingType?.name}"? This action cannot be undone and may affect existing question banks.`}
      />
    </PageShell>
  );
};

export default QuestionTypeManagementPage;